import React from 'react';
import { 
  Paper, 
  Typography, 
  Switch, 
  FormControl, 
  Select, 
  MenuItem, 
  Box,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '80px',
  left: '20px',
  padding: theme.spacing(2),
  minWidth: '280px',
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    top: '60px',
    left: '10px',
    right: '10px',
    minWidth: 'auto',
    maxWidth: 'none',
    width: 'calc(100vw - 188px)',
  },
}));

const DemographicsSelector = ({ selectedDemographic, onDemographicChange, visible, onToggle, isActive, isLoading }) => {
  if (!visible) return null;

  const demographicOptions = [
    { value: 'hispanic_percentage', label: 'Hispanic Population %', description: 'Percentage of Hispanic/Latino population', color: '#ff0000' },
    { value: 'white_percentage', label: 'White Population %', description: 'Percentage of White (non-Hispanic) population', color: '#0096ff' },
    { value: 'black_percentage', label: 'Black Population %', description: 'Percentage of Black/African American (non-Hispanic) population', color: '#9600ff' },
    { value: 'asian_percentage', label: 'Asian Population %', description: 'Percentage of Asian (non-Hispanic) population', color: '#00ff00' },
    { value: 'mixed_percentage', label: 'Mixed Race %', description: 'Percentage of Two or More Races (non-Hispanic) population', color: '#ff6400' },
    { value: 'total_population', label: 'Total Population', description: 'Total population count', color: '#ffff00' }
  ];

  const selectedOption = demographicOptions.find(opt => opt.value === selectedDemographic);

  return (
    <StyledPaper elevation={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight="600" color="white" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
          Demographics Overlay
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {isLoading && (
            <CircularProgress 
              size={16} 
              sx={{ color: 'white' }}
            />
          )}
          <Switch
            checked={isActive}
            onChange={onToggle}
            size="small"
            disabled={isLoading}
            sx={{
              '& .MuiSwitch-switchBase': {
                color: isActive ? '#4CAF50' : '#f44336',
              },
              '& .MuiSwitch-track': {
                backgroundColor: isActive ? '#4CAF50' : '#f44336',
              },
              '& .MuiSwitch-thumb': {
                backgroundColor: isActive ? '#4CAF50' : '#f44336',
              },
            }}
          />
        </Box>
      </Box>
      
      {isActive && (
        <>
          <Divider sx={{ my: 1 }} />
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <Select
              value={selectedDemographic}
              onChange={(e) => onDemographicChange(e.target.value)}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  color: 'white',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                },
              }}
              renderValue={(value) => {
                const option = demographicOptions.find(opt => opt.value === value);
                return (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      width={12}
                      height={12}
                      borderRadius="50%"
                      sx={{ backgroundColor: option?.color }}
                    />
                    <Typography variant="body2" color="white">{option?.label}</Typography>
                  </Box>
                );
              }}
            >
              {demographicOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      width={12}
                      height={12}
                      borderRadius="50%"
                      sx={{ backgroundColor: option.color }}
                    />
                    <Typography variant="body2" color="white">{option.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* <Typography variant="caption" color="rgba(255, 255, 255, 0.8)" sx={{ lineHeight: 1.4, fontWeight: '500' }}>
            {selectedOption?.description}
          </Typography> */}
        </>
      )}
    </StyledPaper>
  );
};

export default DemographicsSelector; 